import React from 'react';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import dayjs, { Dayjs } from 'dayjs';

type DateTimeMode = 'start' | 'end';

type DateTimePickerProps = {
  /** 'start' = 開始日 (T00:00:00), 'end' = 終了日 (T23:59:59) */
  mode: DateTimeMode;
  /** date-time 形式の値 (yyyy-MM-dd'T'HH:mm:ss) または空文字 */
  value: string;
  /** date-time 形式の値を返す */
  onChange: (value: string) => void;
  /** ラベル */
  label?: string;
  /** 無効状態 */
  disabled?: boolean;
  /** 必須表示 */
  required?: boolean;
  /** エラー表示 */
  error?: boolean;
  /** エラーメッセージ */
  helperText?: string;
  /** フル幅 */
  fullWidth?: boolean;
  /** サイズ */
  size?: 'small' | 'medium';
};

/**
 * date-time 形式 (yyyy-MM-dd'T'HH:mm:ss) を強制するカスタムデートピッカー。
 *
 * MUI X DatePicker ベースでアプリのデザインに統一。
 * - mode="start" → T00:00:00 を付加
 * - mode="end"   → T23:59:59 を付加
 */
const DateTimePicker: React.FC<DateTimePickerProps> = ({
  mode,
  value,
  onChange,
  label,
  disabled = false,
  required = false,
  error = false,
  helperText,
  fullWidth = false,
  size = 'small',
}) => {
  const timeSuffix = mode === 'start' ? 'T00:00:00' : 'T23:59:59';

  const dayjsValue: Dayjs | null = value ? dayjs(value.substring(0, 10)) : null;

  const handleChange = (newValue: Dayjs | null) => {
    if (newValue && newValue.isValid()) {
      onChange(`${newValue.format('YYYY-MM-DD')}${timeSuffix}`);
    } else {
      onChange('');
    }
  };

  return (
    <DatePicker
      label={label}
      value={dayjsValue}
      onChange={handleChange}
      disabled={disabled}
      format="YYYY/MM/DD"
      slotProps={{
        textField: {
          size,
          fullWidth,
          required,
          error,
          helperText,
          sx: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          },
        },
      }}
    />
  );
};

export default DateTimePicker;
